import React from "react";

import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmPopup } from "primereact/confirmpopup";
import { DataTable } from "primereact/datatable";
import { T } from "services/lang/LanguageService";
import { TaskTemplateInputFromSubgroup } from "../../../../../api/model/airflow-task/task-template/TaskTemplateInfo";
import InputGroupedDefinitionsSubFormsListDialog from "./InputGroupedDefinitionsSubFormsListDialog";
import { renderDeleteButton, renderHeaderInput } from "./TemplateEditorUtils";

type InputGroupedDefinitionsSubFormsListProps = {
  subForms: TaskTemplateInputFromSubgroup[];
  onSubFormsChange: React.Dispatch<React.SetStateAction<TaskTemplateInputFromSubgroup[]>>;
};

const defaultSubForm: TaskTemplateInputFromSubgroup = {
  header: "",
  inputs: [],
};

const InputGroupedDefinitionsSubFormsList: React.FC<InputGroupedDefinitionsSubFormsListProps> = ({
  subForms,
  onSubFormsChange,
}) => {
  const confirmOnDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
    confirmPopup({
      target: e.currentTarget,
      message: T("confirm-popup.are-you-sure-you-want-to-delete-this-subform"),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => deleteColumn(index),
    });
  };

  const deleteColumn = (index: number) => {
    const newSubForms = subForms.filter((_, i) => i !== index);
    onSubFormsChange(newSubForms);
  };

  const updateSubFormHeader = (index: number, value: string) => {
    onSubFormsChange(prev =>
      prev.map((subForm, i) => (i === index ? { ...subForm, header: value } : subForm))
    );
  };

  const updateSubFormInputs = (newSubForm: TaskTemplateInputFromSubgroup, index: number) => {
    const updatedSubForms = subForms.map((subForm, i) => (i === index ? newSubForm : subForm));
    onSubFormsChange(updatedSubForms);
  };

  const renderInputsDialog = (
    rowData: TaskTemplateInputFromSubgroup,
    options: any,
    onSubFormChange: (newSubForm: TaskTemplateInputFromSubgroup, index: number) => void
  ) => (
    <InputGroupedDefinitionsSubFormsListDialog
      key={rowData.header + options.rowIndex}
      subForm={rowData}
      index={options.rowIndex}
      onSubFormChange={onSubFormChange}
    />
  );

  return (
    <>
      <DataTable
        value={subForms}
        size="small"
        editMode="row"
        reorderableRows
        onRowReorder={e => onSubFormsChange(e.value)}
      >
        <Column
          field="header"
          header={T("label.form-name")}
          body={(rowData, options) => renderHeaderInput(rowData, options, updateSubFormHeader)}
        />
        <Column
          field="inputs"
          align="right"
          header={T("word.inputs")}
          body={(rowData, options) => renderInputsDialog(rowData, options, updateSubFormInputs)}
        />
        <Column style={{ width: "0" }} body={renderDeleteButton(confirmOnDelete)} />
        <Column rowReorder style={{ width: "3em", textAlign: "center" }} />
      </DataTable>
      <div className="flex mt-3">
        <Button
          icon="pi pi-plus"
          label={T("action.add-form")}
          className="component-editor-button icon-green w-max"
          onClick={() => onSubFormsChange([...subForms, defaultSubForm])}
        />
      </div>
    </>
  );
};

export default InputGroupedDefinitionsSubFormsList;
